import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, watch } from 'vue';
import { api, type Locale } from './api';
import { isSignedIn } from './session';
import { setLocale } from './i18n';

/**
 * The signed-in person, once per load (GET /v1/me also refreshes the account
 * from the token on the server — that is where a verified address picks up a
 * waiting grant). Their stored language wins over the browser's (§8.1).
 */
export function useMe() {
  const query = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    enabled: isSignedIn,
  });
  watch(
    () => query.data.value?.locale,
    (locale) => locale && setLocale(locale),
    { immediate: true },
  );
  return {
    ...query,
    entitled: computed(() =>
      Boolean(query.data.value?.entitlement.active || query.data.value?.isPlatformAdmin),
    ),
  };
}

/** The language switch: stored on the account when signed in, else just applied. */
export function useLocaleSwitch() {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: (locale: Locale) => api.updateMe({ locale }),
    onSuccess: (me) => client.setQueryData(['me'], me),
  });
  return (locale: Locale) => {
    setLocale(locale);
    if (isSignedIn.value) mutation.mutate(locale);
  };
}
